import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  VStack,
} from '@wegroup/design-system';
import React from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { PartyType } from '../../../../../../../../types/SMT';
import useTracking from '../../../../../../../common/src/hooks/tracking/useTracking';
import { Option } from '../../../../../../../design-system/src/content/components/SearchPickerInput';
// import { Droppable } from 'react-beautiful-dnd';
import {
  InsurancePolicy,
  InsurancePolicyType,
} from '../../../../../types/AdvisoryReport';
import { useChangeInsurancePolicyOrder } from '../../../api/current-insurances/changeInsurancePolicyOrder';
import useAdvisoryReportCreationParams from '../../../hooks/useCreationFlowSubject';
import InsurancePolicyItem from './insurance-policy/InsurancePolicyItem';
import InsurancePolicyVirtualList from './insurance-policy/InsurancePolicyVirtualList';

export interface InsurancePolicyOverviewProps {
  insurancePolicies: InsurancePolicy[];
  withActions?: boolean;
  type?: InsurancePolicyType;
  customRiskObjects?: Option[];
  partyId?: string;
  partyType: PartyType;
  controlled?: boolean;
  renderCustomActions?: (insurancePolicy: InsurancePolicy) => React.ReactNode;

  // Actions
  onEdit?: (insurancePolicy: InsurancePolicy) => void;
  onRemove?: (id: string) => void;
}

export const POLICIES_PAGE_SIZE = 15;

const InsurancePolicyOverview: React.FC<InsurancePolicyOverviewProps> = (
  props,
) => {
  const { t } = useTranslation();
  const {
    insurancePolicies,
    withActions,
    type,
    controlled,
    renderCustomActions,
  } = props;
  const { reportId } = useAdvisoryReportCreationParams();
  const { trackEvent } = useTracking();
  const isCurrentInsurance = type
    ? type === 'CURRENT-INSURANCE'
    : insurancePolicies?.[0]?.type === 'ACTIVE';
  const { mutateAsync: changeInsurancePolicyOrder } =
    useChangeInsurancePolicyOrder();
  const isDropDisabled = !withActions || controlled;

  const reorder = (
    list: InsurancePolicy[],
    startIndex: number,
    endIndex: number,
  ) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const newOrder = reorder(
      insurancePolicies,
      result.source.index,
      result.destination.index,
    );

    trackEvent(
      'Change order for insurance policies in advisory report creation flow',
    );
    const payloadKey = isCurrentInsurance ? 'current_insurances' : 'offers';
    changeInsurancePolicyOrder({
      [payloadKey]: newOrder.map(({ id }) => id),
      reportId,
    });
  };

  if (isDropDisabled || insurancePolicies.length > POLICIES_PAGE_SIZE) {
    return (
      <VStack align="stretch" spacing="3">
        {!isDropDisabled && (
          <Alert status="info" py="3">
            <AlertIcon alignSelf="flex-start" />
            <div>
              <AlertTitle>
                {t('INSURANCE-POLICIES_D&D_DISABLED_TITLE')}
              </AlertTitle>
              <AlertDescription textStyle="smallBody" lineHeight="auto">
                {t('INSURANCE-POLICIES_D&D_DISABLED_DESCRIPTION')}
              </AlertDescription>
            </div>
          </Alert>
        )}
        <InsurancePolicyVirtualList
          {...props}
          insurancePolicies={insurancePolicies}
          renderCustomActions={renderCustomActions}
        />
      </VStack>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="droppable">
        {(provided) => (
          <Box {...provided.droppableProps} ref={provided.innerRef}>
            {insurancePolicies.map((insurancePolicy, index) => (
              <InsurancePolicyItem
                key={insurancePolicy.id}
                insurancePolicy={insurancePolicy}
                index={index}
                customActions={renderCustomActions?.(insurancePolicy)}
                {...props}
              />
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default InsurancePolicyOverview;
